import React, {useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {useTranslation} from "react-i18next";
import {SearchTable} from "../../../../components/SearchTable";
import {OvalSpinner} from "../../../../components/shared/OvalSpinner";
import {ToggleButton} from "primereact/togglebutton";
import "./Admins.styles.scss";
import {Paginator} from "primereact/paginator";

const Admins = ({
                    admins,
                    handleStatusChange,
                    pageSize,
                    totalItems,
                    activePage,
                    onChangePage,
                    onSearchHandler,
                    loading,
                    onDeleteClick,
                    editAdminPhoneNumber,
                }) => {
    const {t} = useTranslation();
    const [isConfirmationVisible, setConfirmationVisible] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [isPhoneEditVisible, setPhoneEditVisible] = useState(false);
    const [phoneEditData, setPhoneEditData] = useState(null);
    const [newPhoneNumber, setNewPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");

    const onPageChangeHandler = (p) => {
        onChangePage(p.page + 1);
    };

    const handleDeleteConfirm = () => {
        onDeleteClick(selectedRowData);
        setConfirmationVisible(false);
        setSelectedRowData(null);
    };

    const handleDeleteCancel = () => {
        setConfirmationVisible(false);
        setSelectedRowData(null);
    };

    const handlePhoneEditClick = (rowData) => {
        setPhoneEditData(rowData);
        setNewPhoneNumber(rowData.phoneNumber && rowData.phoneNumber !== "N/A" ? rowData.phoneNumber : "");
        setPhoneError("");
        setPhoneEditVisible(true);
    };

    const handlePhoneEditCancel = () => {
        setPhoneEditVisible(false);
        setPhoneEditData(null);
        setNewPhoneNumber("");
        setPhoneError("");
    };

    const validatePhoneNumber = (phone) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    const handlePhoneEditConfirm = () => {
        if (!newPhoneNumber.trim()) {
            setPhoneError(t("admins.phoneRequired"));
            return;
        }

        if (!validatePhoneNumber(newPhoneNumber)) {
            setPhoneError(t("admins.phoneValidation"));
            return;
        }

        const updatedAdminData = {
            ...phoneEditData,
            phoneNumber: newPhoneNumber
        };

        editAdminPhoneNumber(updatedAdminData);
        handlePhoneEditCancel();
    };

    return (<div className="AdminsContainer">
        <div className="d-flex justify-content-between align-items-center mb-4">
            <SearchTable
                placeholder={t("admins.searchPlaceholder")}
                onSearchHandler={onSearchHandler}
            />
        </div>

        <DataTable
            value={admins}
            responsiveLayout="scroll"
            rowGroupMode="rowspan"
        >
            <Column field="id" header={t("admins.id")} sortable/>
            <Column field="username" header={t("admins.username")} sortable/>
            <Column
                field="phoneNumber"
                header={t("admins.phoneNumber")}
                sortable
                body={(rowData) => (
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <span>{rowData.phoneNumber || "N/A"}</span>

                        <i
                            className="pi pi-pencil"
                            style={{cursor: 'pointer', fontSize: '14px', color: '#3B82F6'}}
                            onClick={() => handlePhoneEditClick(rowData)}
                            title={t("admins.editPhone")}
                        />
                    </div>
                )}
            />

            <Column
                header={t("admins.enableDisableAdmins")}
                sortable
                body={(rowData) => loading[rowData?.status] ? (<OvalSpinner width={35} height={35}/>) : (
                    <ToggleButton
                        checked={rowData.status === true}
                        onChange={(e) => handleStatusChange(e.value, rowData)}
                        onIcon="pi pi-enabled"
                        offIcon="pi pi-disabled"
                        onLabel=""
                        offLabel=""
                        disabled={rowData.superAdmin === true}
                    />)}
            />
            <Column
                header={t("admins.delete")}
                body={(rowData) => (<button
                    className="delete-button"
                    disabled={rowData.superAdmin === true}
                    onClick={() => {
                        setSelectedRowData(rowData);
                        setConfirmationVisible(true);
                    }}
                >
                    Delete
                </button>)}
            />
        </DataTable>
        <Paginator
            first={activePage * pageSize}
            pageLinkSize={7}
            rows={pageSize}
            totalRecords={totalItems}
            onPageChange={onPageChangeHandler}
        />

        {/* Enhanced confirmation dialog with proper styling */}
        {isConfirmationVisible && selectedRowData && (
            <div className="confirmation-overlay" onClick={handleDeleteCancel}>
                <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                    <p>Are you sure you want to delete user "{selectedRowData.username}"?</p>
                    <p style={{fontSize: '14px', color: '#666', marginTop: '8px'}}>
                        This action cannot be undone.
                    </p>
                    <div className="modal-buttons">
                        <button
                            className="btn btn-danger"
                            onClick={handleDeleteConfirm}
                            autoFocus
                        >
                            Delete
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleDeleteCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>)}

        {/* Phone edit dialog */}
        {isPhoneEditVisible && phoneEditData && (
            <div className="confirmation-overlay" onClick={handlePhoneEditCancel}>
                <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                    <p>Edit phone number for "{phoneEditData.username}"</p>
                    {phoneEditData.phoneNumber && phoneEditData.phoneNumber !== "N/A" && (
                        <p style={{fontSize: '14px', color: '#666', marginTop: '8px'}}>
                            Current: {phoneEditData.phoneNumber}
                        </p>
                    )}
                    <div style={{marginTop: '16px', marginBottom: '16px'}}>
                        <input
                            type="text"
                            value={newPhoneNumber}
                            onChange={(e) => {
                                setNewPhoneNumber(e.target.value);
                                setPhoneError("");
                            }}
                            placeholder="Enter 10-digit phone number"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                border: phoneError ? '1px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '4px',
                                outline: 'none'
                            }}
                            autoFocus
                        />
                        {phoneError && (
                            <p style={{color: '#dc3545', fontSize: '12px', marginTop: '5px'}}>
                                {phoneError}
                            </p>
                        )}
                    </div>
                    <div className="modal-buttons">
                        <button
                            className="btn btn-primary"
                            onClick={handlePhoneEditConfirm}
                        >
                            Save
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handlePhoneEditCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>)}
    </div>);
};

export default React.memo(Admins);
